import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useCategories, api } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import NotificationBell from '../../components/common/NotificationBell';
import AddressPickerModal from '../../components/common/AddressPickerModal';
import ProfilePhotoUpload from '../../components/common/ProfilePhotoUpload';
import CoverageAreasInput from '../../components/common/CoverageAreasInput';
import DocumentUpload from '../../components/common/DocumentUpload';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import { 
  ArrowLeft, 
  Upload, CheckCircle, Clock, XCircle, Camera, FileText, LogOut, Star, MapPin,
  Plus, Pencil, Trash2, Image, DollarSign, Save, Package, Award, Users, Building2, Phone, User,
  Settings, HelpCircle, ChevronRight, CalendarClock, Shield, AlertTriangle, Sparkles, Info
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const individualDocTypes = [
  { id: 'id', name: 'Valid ID', description: 'Government-issued ID (Passport, Driver\'s License, etc.)', icon: FileText, required: true },
  { id: 'proof_of_address', name: 'Proof of Address', description: 'Utility bill or bank statement', icon: FileText, required: false },
  { id: 'nbi_clearance', name: 'NBI Clearance', description: 'NBI clearance certificate', icon: FileText, required: false },
];

const companyDocTypes = [
  { id: 'dti', name: 'DTI Registration', description: 'Department of Trade and Industry registration', icon: Building2, required: true },
  { id: 'business_permit', name: 'Business Permit', description: 'Mayor\'s permit or business permit', icon: FileText, required: true },
  { id: 'id', name: 'Owner/Rep Valid ID', description: 'Government-issued ID of the authorized representative', icon: FileText, required: true },
  { id: 'bir', name: 'BIR Registration', description: 'Bureau of Internal Revenue registration (optional)', icon: FileText, required: false },
];

export default function ProviderProfile() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { categories } = useCategories();
  const fileInputRef = useRef(null);
  const servicePhotoRef = useRef(null);
  
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [providerServices, setProviderServices] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [showInsuranceDialog, setShowInsuranceDialog] = useState(false);
  const [insuranceSettings, setInsuranceSettings] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [savingService, setSavingService] = useState(false);
  const [savingCert, setSavingCert] = useState(false);
  const [savingStaff, setSavingStaff] = useState(false);
  
  const [formData, setFormData] = useState({
    services: [], skills: [],
    hourly_rate: '', bio: '',
    coverage_areas: [], service_radius_km: 10,
    service_latitude: null, service_longitude: null,
    service_province: '', service_city: '', service_barangay: '',
    first_name: '', last_name: '', middle_name: '',
    birthday: '', present_home_address: '', beneficiary_name: ''
  });
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  
  const [serviceForm, setServiceForm] = useState({
    category_id: '', name: '', description: '', price: '', duration_hours: '1', photo_url: '',
    has_warranty: false, warranty_days: 0, warranty_description: ''
  });
  const [warrantyAnalysis, setWarrantyAnalysis] = useState(null);
  const [analyzingWarranty, setAnalyzingWarranty] = useState(false);
  
  const [certForm, setCertForm] = useState({ title: '', issuer: '', year_obtained: '' });
  const [staffForm, setStaffForm] = useState({ name: '', email: '', phone: '', role: 'technician', skills: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [profileRes, docsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/providers/${user.id}`),
          api.getDocuments(),
          axios.get(`${API}/provider/services`, { headers })
        ]);
        setProfile(profileRes.data);
        setDocuments(docsRes.data);
        setProviderServices(servicesRes.data);
        // Fetch certifications
        try {
          const certsRes = await axios.get(`${API}/provider/certifications`, { headers });
          setCertifications(certsRes.data);
        } catch (e) {
          console.error('Profile: failed to fetch certifications', e);
        }
        // Fetch staff if company
        if (profileRes.data.provider_type === 'company') {
          try {
            const staffRes = await axios.get(`${API}/provider/staff`, { headers });
            setStaffMembers(staffRes.data);
          } catch (e) {
            console.error('Profile: failed to fetch staff members', e);
          }
        }
        try {
          const reviewsRes = await api.getProviderReviews(user.id);
          setReviews(reviewsRes.data);
        } catch (e) {
          console.error('Profile: failed to fetch reviews', e);
        }
        // Fetch insurance settings
        try {
          const insuranceRes = await axios.get(`${API}/insurance/settings`);
          if (insuranceRes.data && insuranceRes.data.enabled && insuranceRes.data.provider_insurance_enabled) {
            setInsuranceSettings(insuranceRes.data);
          }
        } catch (e) {
          console.error('Profile: failed to fetch insurance settings', e);
        }
        setFormData({
          services: profileRes.data.services || [], skills: profileRes.data.skills || [],
          hourly_rate: profileRes.data.hourly_rate || '', bio: profileRes.data.bio || '',
          coverage_areas: profileRes.data.coverage_areas || [],
          service_radius_km: profileRes.data.service_radius_km ?? 10,
          service_latitude: profileRes.data.service_latitude || null,
          service_longitude: profileRes.data.service_longitude || null,
          service_province: profileRes.data.service_province || '',
          service_city: profileRes.data.service_city || '',
          service_barangay: profileRes.data.service_barangay || '',
          first_name: profileRes.data.first_name || '',
          last_name: profileRes.data.last_name || '',
          middle_name: profileRes.data.middle_name || '',
          birthday: profileRes.data.birthday || '',
          present_home_address: profileRes.data.present_home_address || '',
          beneficiary_name: profileRes.data.beneficiary_name || ''
        });
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setIsLoading(false);
      }
    };
    if (user?.id) fetchData();
  }, [user?.id]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await api.updateProfile({
        user_id: user.id, ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      });
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDocumentUpload = async (file, docType) => {
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      throw new Error('Please upload an image or PDF file');
    }
    if (file.size > 10 * 1024 * 1024) { 
      throw new Error('File size must be less than 10MB'); 
    }
    setUploadingType(docType);
    try {
      const fd = new FormData(); 
      fd.append('file', file);
      await api.uploadDocument(fd, { document_type: docType });
      const docsRes = await api.getDocuments(); 
      setDocuments(docsRes.data);
    } finally { 
      setUploadingType(null); 
    }
  };

  // Provider Services CRUD
  const openAddService = () => {
    setEditingService(null);
    setServiceForm({ category_id: categories[0]?.id || '', name: '', description: '', price: '', duration_hours: '1', photo_url: '', has_warranty: false, warranty_days: 0, warranty_description: '' });
    setWarrantyAnalysis(null);
    setShowServiceDialog(true);
  };

  const openEditService = (svc) => {
    setEditingService(svc);
    setServiceForm({
      category_id: svc.category_id, name: svc.name, description: svc.description || '',
      price: String(svc.price), duration_hours: String(svc.duration_hours), photo_url: svc.photo_url || '',
      has_warranty: svc.has_warranty || false, warranty_days: svc.warranty_days || 0, warranty_description: svc.warranty_description || ''
    });
    setWarrantyAnalysis(null);
    setShowServiceDialog(true);
  };

  const handleSaveService = async () => {
    if (!serviceForm.name || !serviceForm.price || !serviceForm.category_id) {
      toast.error('Please fill in service name, category, and price'); return;
    }
    setSavingService(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const payload = {
      category_id: serviceForm.category_id, name: serviceForm.name,
      description: serviceForm.description, price: parseFloat(serviceForm.price),
      duration_hours: parseFloat(serviceForm.duration_hours) || 1,
      photo_url: serviceForm.photo_url || null,
      has_warranty: serviceForm.has_warranty || false,
      warranty_days: parseInt(serviceForm.warranty_days) || 0,
      warranty_description: serviceForm.warranty_description || null
    };
    try {
      if (editingService) {
        await axios.put(`${API}/provider/services/${editingService.id}`, payload, { headers });
        toast.success('Service updated!');
      } else {
        await axios.post(`${API}/provider/services`, payload, { headers });
        toast.success('Service added!');
      }
      setShowServiceDialog(false);
      const res = await axios.get(`${API}/provider/services`, { headers });
      setProviderServices(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save service');
    } finally { setSavingService(false); }
  };

  // AI Warranty Analysis
  const analyzeWarranty = async () => {
    if (!serviceForm.name) {
      toast.error('Please enter a service name first');
      return;
    }
    setAnalyzingWarranty(true);
    const token = localStorage.getItem('token');
    try {
      const categoryName = categories.find(c => c.id === serviceForm.category_id)?.name || '';
      const res = await axios.post(`${API}/provider/services/analyze-warranty`, null, {
        params: {
          service_name: serviceForm.name,
          service_description: serviceForm.description || '',
          category_name: categoryName
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarrantyAnalysis(res.data);
      
      // Auto-apply suggestion if AI recommends warranty
      if (res.data.needs_warranty) {
        setServiceForm(prev => ({
          ...prev,
          has_warranty: true,
          warranty_days: res.data.recommended_warranty_days || 14,
          warranty_description: res.data.warranty_description_suggestion || ''
        }));
        toast.success('Warranty suggestion applied!');
      }
    } catch (e) {
      toast.error('Failed to analyze warranty');
    } finally {
      setAnalyzingWarranty(false);
    }
  };

  const handleDeleteService = async (svcId) => {
    if (!window.confirm('Remove this service?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/provider/services/${svcId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Service removed');
      setProviderServices(prev => prev.filter(s => s.id !== svcId));
    } catch (e) { toast.error('Failed to remove service'); }
  };

  const handleServicePhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    
    if (editingService) {
      // Upload to service photo endpoint
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('token');
      try {
        const res = await axios.post(`${API}/provider/services/${editingService.id}/photo`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        setServiceForm(prev => ({ ...prev, photo_url: res.data.photo_url }));
        toast.success('Photo uploaded!');
      } catch (e) { toast.error('Failed to upload photo'); }
    } else {
      // For new service, we need to save first, then upload photo - use base64 preview for now
      const reader = new FileReader();
      reader.onload = (e) => setServiceForm(prev => ({ ...prev, photo_url: e.target.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); toast.success('Logged out successfully'); };
  const getDocumentStatus = (docType) => documents.find(d => d.document_type === docType)?.status || null;
  const getDocumentRejectionReason = (docType) => documents.find(d => d.document_type === docType)?.rejection_reason || null;
  
  const providerType = profile?.provider_type || 'individual';
  const isCompany = providerType === 'company';
  const documentTypes = isCompany ? companyDocTypes : individualDocTypes;
  
  // Certifications
  const handleAddCert = async () => {
    if (!certForm.title) { toast.error('Title is required'); return; }
    setSavingCert(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/provider/certifications`, certForm, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      setCertifications(prev => [...prev, res.data]);
      setShowCertDialog(false);
      setCertForm({ title: '', issuer: '', year_obtained: '' });
      toast.success('Certification added!');
    } catch (e) { toast.error('Failed to add certification'); }
    finally { setSavingCert(false); }
  };

  const handleUploadCertFile = async (certId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/provider/certifications/${certId}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setCertifications(prev => prev.map(c => c.id === certId ? { ...c, file_url: res.data.file_url } : c));
      toast.success('Certificate file uploaded!');
    } catch (e) { toast.error('Upload failed'); }
  };

  const handleDeleteCert = async (certId) => {
    if (!window.confirm('Remove this certification?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/provider/certifications/${certId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCertifications(prev => prev.filter(c => c.id !== certId));
      toast.success('Certification removed');
    } catch (e) { toast.error('Failed to remove'); }
  };

  // Staff Management (Company only)
  const openAddStaff = () => {
    setEditingStaff(null);
    setStaffForm({ name: '', email: '', phone: '', role: 'technician', skills: [] });
    setShowStaffDialog(true);
  };

  const openEditStaff = (s) => {
    setEditingStaff(s);
    setStaffForm({ name: s.name, email: s.email || '', phone: s.phone || '', role: s.role || 'technician', skills: s.skills || [] });
    setShowStaffDialog(true);
  };

  const handleSaveStaff = async () => {
    if (!staffForm.name) { toast.error('Name required'); return; }
    setSavingStaff(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      if (editingStaff) {
        const res = await axios.put(`${API}/provider/staff/${editingStaff.id}`, staffForm, { headers });
        setStaffMembers(prev => prev.map(s => s.id === editingStaff.id ? res.data : s));
        toast.success('Staff updated');
      } else {
        const res = await axios.post(`${API}/provider/staff`, staffForm, { headers });
        setStaffMembers(prev => [...prev, res.data]);
        toast.success('Staff added');
      }
      setShowStaffDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingStaff(false); }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Remove this staff member?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/provider/staff/${staffId}`, { headers: { Authorization: `Bearer ${token}` } });
      setStaffMembers(prev => prev.filter(s => s.id !== staffId));
      toast.success('Staff removed');
    } catch (e) { toast.error('Failed to remove'); }
  };

  if (isLoading) {
    return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;
  }

  const verificationStatus = profile?.verification_status;

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-12 px-4 rounded-b-3xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/provider')} className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-xl font-bold text-white font-[Poppins]">Profile</h1>
          </div>
          <NotificationBell />
        </div>
        <div className="flex items-center gap-4">
          <ProfilePhotoUpload
            currentImage={user?.profile_image}
            userName={isCompany ? (profile?.company_name || user?.name) : user?.name}
            onImageUpdate={() => refreshUser?.()}
            size="md"
          />
          <div>
            <h2 className="text-xl font-bold text-white">{isCompany ? (profile?.company_name || user?.name) : (user?.name || 'Provider')}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isCompany ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/20 text-amber-300'}`}>
                {isCompany ? <><Building2 className="w-3 h-3" /> Company</> : <><User className="w-3 h-3" /> Individual</>}
              </span>
              {verificationStatus === 'verified' ? (
                <span className="inline-flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="w-4 h-4" /> Verified</span>
              ) : verificationStatus === 'pending' ? (
                <span className="inline-flex items-center gap-1 text-amber-400 text-sm"><Clock className="w-4 h-4" /> Pending</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-red-400 text-sm"><XCircle className="w-4 h-4" /> Not Verified</span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 -mt-6 pb-24">
        {/* Verification Documents */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">Verification Documents</h3>
          <div className="space-y-3">
            {documentTypes.map(doc => {
              const status = getDocumentStatus(doc.id);
              const rejectionReason = getDocumentRejectionReason(doc.id);
              return (
                <DocumentUpload
                  key={doc.id}
                  docType={doc.id}
                  docName={doc.name}
                  docDescription={doc.description}
                  status={status}
                  rejectionReason={rejectionReason}
                  isUploading={uploadingType === doc.id}
                  onUpload={handleDocumentUpload}
                  icon={doc.icon}
                  acceptTypes="image/*,.pdf"
                />
              );
            })}
          </div>
        </section>

        {/* My Services */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="my-services-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-slate-900 font-[Poppins]">My Services</h3>
            </div>
            <Button size="sm" onClick={openAddService} className="bg-slate-900 hover:bg-slate-800" data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>

          {providerServices.length === 0 ? (
            <div className="text-center py-8 bg-stone-50 rounded-xl">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No services yet</p>
              <p className="text-slate-400 text-xs">Add services you offer to attract customers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {providerServices.map(svc => (
                <div key={svc.id} className="flex gap-3 p-3 bg-stone-50 rounded-xl" data-testid={`provider-service-${svc.id}`}>
                  <div className="w-16 h-16 bg-stone-200 rounded-lg overflow-hidden flex-shrink-0">
                    {svc.photo_url ? (
                      <img src={svc.photo_url.startsWith('http') || svc.photo_url.startsWith('data:') ? svc.photo_url : `${BACKEND_URL}${svc.photo_url}`} alt={svc.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-slate-400" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{svc.name}</p>
                    <p className="text-xs text-amber-600">{svc.category_name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm font-bold text-green-700">₱{svc.price.toLocaleString()}</span>
                      <span className="text-xs text-slate-500">{svc.duration_hours}h</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditService(svc)} data-testid={`edit-svc-${svc.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteService(svc.id)} data-testid={`del-svc-${svc.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Certifications */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="certifications-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-slate-900 font-[Poppins]">Certifications</h3>
            </div>
            <Button size="sm" onClick={() => { setCertForm({ title: '', issuer: '', year_obtained: '' }); setShowCertDialog(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-cert-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {certifications.length === 0 ? (
            <div className="text-center py-6 bg-stone-50 rounded-xl">
              <Award className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No certifications yet</p>
              <p className="text-slate-400 text-xs">Upload certifications to build trust with customers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {certifications.map(cert => (
                <div key={cert.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl" data-testid={`cert-${cert.id}`}>
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{cert.title}</p>
                    {cert.issuer && <p className="text-xs text-slate-500">{cert.issuer} {cert.year_obtained && `(${cert.year_obtained})`}</p>}
                    {cert.file_url ? (
                      <a href={`${BACKEND_URL}${cert.file_url}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View document</a>
                    ) : (
                      <label className="text-xs text-amber-600 cursor-pointer hover:underline">
                        Upload file
                        <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => handleUploadCertFile(cert.id, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteCert(cert.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Staff Management (Company Only) */}
        {isCompany && (
          <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="staff-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                <h3 className="font-bold text-slate-900 font-[Poppins]">Staff Members</h3>
              </div>
              <Button size="sm" onClick={openAddStaff} className="bg-slate-900 hover:bg-slate-800" data-testid="add-staff-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Staff
              </Button>
            </div>
            {staffMembers.length === 0 ? (
              <div className="text-center py-6 bg-stone-50 rounded-xl">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No staff yet</p>
                <p className="text-slate-400 text-xs">Add your team members who will render services</p>
              </div>
            ) : (
              <div className="space-y-3">
                {staffMembers.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl" data-testid={`staff-${s.id}`}>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-blue-700">{s.name.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm">{s.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{s.role}</p>
                      {s.skills?.length > 0 && <p className="text-xs text-amber-600">{s.skills.join(', ')}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditStaff(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteStaff(s.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Service Area Map */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="service-area-section">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-slate-900 font-[Poppins]">Service Location</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">Set your base location so customers near you can find your services.</p>
          
          {formData.service_latitude && formData.service_longitude ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">Location set</span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                {[formData.service_barangay, formData.service_city, formData.service_province].filter(Boolean).join(', ') || `${formData.service_latitude.toFixed(4)}, ${formData.service_longitude.toFixed(4)}`}
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">No location set</span>
              </div>
              <p className="text-xs text-amber-600 mt-1">Tap the button below to set your service location</p>
            </div>
          )}
          
          <Button
            variant="outline"
            className="w-full h-12 border-dashed border-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => setShowLocationPicker(true)}
            data-testid="open-location-picker-btn"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {formData.service_latitude ? 'Change Location' : 'Set Location on Map'}
          </Button>

          {formData.service_latitude && formData.service_longitude && (
            <div className="mt-4">
              <Label className="text-sm text-slate-700">Service radius (km from your base)</Label>
              <p className="text-xs text-slate-500 mb-2">Customers within this distance of your base pin can find you.</p>
              <div className="flex items-center gap-3">
                <input
                  type="range" min="1" max="100" step="1"
                  value={formData.service_radius_km || 10}
                  onChange={(e) => setFormData(prev => ({ ...prev, service_radius_km: parseInt(e.target.value, 10) }))}
                  className="flex-1 accent-amber-500"
                  data-testid="service-radius-slider"
                />
                <span className="w-16 text-sm font-semibold text-amber-700 text-right" data-testid="service-radius-value">
                  {formData.service_radius_km || 10} km
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Coverage Areas */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="coverage-areas-section">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-green-600" />
            <h3 className="font-bold text-slate-900 font-[Poppins]">Coverage Areas</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Add every city or municipality you're willing to serve. This is separate from your base location — customers searching in any of these areas will find you.
          </p>
          <CoverageAreasInput
            value={formData.coverage_areas}
            onChange={(areas) => setFormData(prev => ({ ...prev, coverage_areas: areas }))}
          />
        </section>

        {/* Insurance Details */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="insurance-details-section">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-slate-900 font-[Poppins]">Insurance Details</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            These details are used for HandyBayani's daily insurance declaration so you're covered while on the job. Please keep them accurate.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-slate-700">First Name</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="Juan" className="mt-1" data-testid="insurance-first-name"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Last Name</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Dela Cruz" className="mt-1" data-testid="insurance-last-name"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Middle Name</Label>
              <Input
                value={formData.middle_name}
                onChange={(e) => setFormData(prev => ({ ...prev, middle_name: e.target.value }))}
                placeholder="Santos" className="mt-1" data-testid="insurance-middle-name"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Birthday</Label>
              <Input
                type="date"
                value={formData.birthday}
                onChange={(e) => setFormData(prev => ({ ...prev, birthday: e.target.value }))}
                className="mt-1" data-testid="insurance-birthday"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm text-slate-700">Present Home Address</Label>
              <Input
                value={formData.present_home_address}
                onChange={(e) => setFormData(prev => ({ ...prev, present_home_address: e.target.value }))}
                placeholder="House no., street, barangay, city, province" className="mt-1" data-testid="insurance-home-address"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm text-slate-700">Name of Beneficiary</Label>
              <Input
                value={formData.beneficiary_name}
                onChange={(e) => setFormData(prev => ({ ...prev, beneficiary_name: e.target.value }))}
                placeholder="e.g. Maria Dela Cruz (Spouse)" className="mt-1" data-testid="insurance-beneficiary"
              />
              <p className="text-xs text-slate-400 mt-1">Legal spouse, children, parents, or siblings.</p>
            </div>
          </div>
        </section>

        {/* Bio */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">About You</h3>
          <Textarea placeholder="Tell customers about your experience and skills..."
            value={formData.bio} onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
            className="min-h-[100px]" data-testid="bio-input" />
        </section>

        {/* Reviews Section */}
        <section className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 font-[Poppins]">Reviews ({reviews.length})</h3>
            {profile?.rating > 0 && <div className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /><span className="font-semibold text-slate-900">{profile.rating}</span></div>}
          </div>
          {reviews.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No reviews yet. Complete jobs to get reviews!</p>
          ) : (
            <div className="space-y-3">
              {reviews.slice(0, 5).map(review => (
                <div key={review.id} className="p-3 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 bg-amber-100 rounded-full flex items-center justify-center"><span className="text-xs font-semibold text-amber-700">{(review.customer_name || 'C')[0]}</span></div>
                    <span className="font-medium text-sm text-slate-900">{review.customer_name || 'Customer'}</span>
                    <div className="flex items-center gap-0.5 ml-auto">
                      {[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />)}
                    </div>
                  </div>
                  {review.comment && <p className="text-sm text-slate-600 ml-9">{review.comment}</p>}
                  <p className="text-xs text-slate-400 ml-9 mt-1">{new Date(review.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <Button className="w-full h-12 bg-slate-900 hover:bg-slate-800 mb-4" onClick={handleSaveProfile} disabled={isSaving} data-testid="save-profile-btn">
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
        
        {/* Quick Links */}
        <div className="bg-white rounded-2xl shadow-sm mb-4 overflow-hidden">
          {/* Verification Banner - Show if not verified */}
          {verificationStatus !== 'verified' && (
            <button 
              onClick={() => navigate('/verification')}
              className="w-full flex items-center justify-between px-4 py-4 bg-amber-50 hover:bg-amber-100 transition-colors border-b border-amber-200"
              data-testid="verification-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                </div>
                <div className="text-left">
                  <span className="font-medium text-amber-900 block">Verify Your Identity</span>
                  <span className="text-xs text-amber-700">Required to accept bookings</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-600" />
            </button>
          )}
          
          {/* Show verified badge if verified */}
          {verificationStatus === 'verified' && (
            <div className="flex items-center gap-3 px-4 py-4 bg-green-50 border-b border-green-200">
              <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <span className="font-medium text-green-900 block">Identity Verified</span>
                <span className="text-xs text-green-700">Your account is verified</span>
              </div>
            </div>
          )}
          
          <button 
            onClick={() => navigate('/provider/availability')}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-stone-50 transition-colors border-b border-stone-100"
            data-testid="availability-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <CalendarClock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <span className="font-medium text-slate-900 block">Availability</span>
                <span className="text-xs text-slate-500">Set your working hours</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* Accident Insurance - Show if enabled */}
          {insuranceSettings && (
            <button 
              onClick={() => setShowInsuranceDialog(true)}
              className="w-full flex items-center justify-between px-4 py-4 hover:bg-stone-50 transition-colors border-b border-stone-100"
              data-testid="insurance-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-900 block">
                    Accident Insurance
                    {insuranceSettings.provider_insurance_mandatory && (
                      <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-medium" data-testid="provider-insurance-mandatory-badge">Required</span>
                    )}
                  </span>
                  <span className="text-xs text-slate-500">
                    {insuranceSettings.provider_insurance_mandatory ? 'Mandatory coverage for all bookings' : 'View your coverage'}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          )}
          
          {/* Staff Management - Only for Company Providers */}
          {profile?.provider_type === 'company' && (
            <button 
              onClick={() => navigate('/provider/staff')}
              className="w-full flex items-center justify-between px-4 py-4 hover:bg-stone-50 transition-colors border-b border-stone-100"
              data-testid="staff-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-900 block">Staff Management</span>
                  <span className="text-xs text-slate-500">Manage your team members</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          )}
          
          <button 
            onClick={() => navigate('/profile/settings')}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-stone-50 transition-colors border-b border-stone-100"
            data-testid="settings-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-slate-600" />
              </div>
              <span className="font-medium text-slate-900">Account Settings</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          <button 
            onClick={() => navigate('/disputes')}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-stone-50 transition-colors"
            data-testid="help-support-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-amber-600" />
              </div>
              <span className="font-medium text-slate-900">Help & Support</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        <Button variant="outline" className="w-full h-12 text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-5 h-5 mr-2" /> Log Out
        </Button>
      </main>

      {/* Add/Edit Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Category</Label>
              <Select value={serviceForm.category_id} onValueChange={(v) => setServiceForm(p => ({ ...p, category_id: v }))}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="svc-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service Name</Label>
              <Input value={serviceForm.name} onChange={e => setServiceForm(p => ({ ...p, name: e.target.value }))} className="h-12 mt-1.5" placeholder="e.g., Kitchen Pipe Repair" data-testid="svc-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={serviceForm.description} onChange={e => setServiceForm(p => ({ ...p, description: e.target.value }))} className="mt-1.5" placeholder="What does this service include?" data-testid="svc-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price (₱)</Label>
                <Input type="number" value={serviceForm.price} onChange={e => setServiceForm(p => ({ ...p, price: e.target.value }))} className="h-12 mt-1.5" placeholder="500" data-testid="svc-price-input" />
              </div>
              <div>
                <Label>Duration (hours)</Label>
                <Input type="number" step="0.5" value={serviceForm.duration_hours} onChange={e => setServiceForm(p => ({ ...p, duration_hours: e.target.value }))} className="h-12 mt-1.5" placeholder="1" data-testid="svc-duration-input" />
              </div>
            </div>
            <div>
              <Label>Service Photo</Label>
              <div className="mt-1.5">
                {serviceForm.photo_url ? (
                  <div className="relative w-full h-32 rounded-lg overflow-hidden bg-stone-100">
                    <img src={serviceForm.photo_url.startsWith('http') || serviceForm.photo_url.startsWith('data:') ? serviceForm.photo_url : `${BACKEND_URL}${serviceForm.photo_url}`} alt="Service" className="w-full h-full object-cover" />
                    <button onClick={() => setServiceForm(p => ({ ...p, photo_url: '' }))} className="absolute top-2 right-2 w-7 h-7 bg-black/50 rounded-full flex items-center justify-center text-white">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:bg-stone-50">
                    <Image className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Upload photo</span>
                    <input ref={servicePhotoRef} type="file" accept="image/*" className="hidden" onChange={handleServicePhoto} />
                  </label>
                )}
              </div>
            </div>
            
            {/* Service Warranty Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  Service Warranty
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={analyzeWarranty}
                  disabled={analyzingWarranty || !serviceForm.name}
                  className="h-8 text-xs"
                  data-testid="analyze-warranty-btn"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {analyzingWarranty ? 'Analyzing...' : 'AI Suggest'}
                </Button>
              </div>
              
              {warrantyAnalysis && (
                <div className={`p-3 rounded-lg mb-3 text-sm ${warrantyAnalysis.needs_warranty ? 'bg-amber-50 border border-amber-200' : 'bg-stone-50 border border-stone-200'}`}>
                  <p className="font-medium text-slate-800 flex items-center gap-2">
                    {warrantyAnalysis.needs_warranty ? (
                      <><CheckCircle className="w-4 h-4 text-green-600" /> AI Recommends Warranty</>
                    ) : (
                      <><Info className="w-4 h-4 text-slate-500" /> Warranty Optional</>
                    )}
                  </p>
                  <p className="text-slate-600 mt-1">{warrantyAnalysis.reasoning}</p>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="hasWarranty"
                  checked={serviceForm.has_warranty}
                  onChange={(e) => setServiceForm(p => ({ ...p, has_warranty: e.target.checked }))}
                  className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  data-testid="has-warranty-checkbox"
                />
                <label htmlFor="hasWarranty" className="text-sm text-slate-700 cursor-pointer">
                  Include warranty for this service
                </label>
              </div>
              
              {serviceForm.has_warranty && (
                <div className="space-y-3 pl-8">
                  <div>
                    <Label>Warranty Period (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={serviceForm.warranty_days}
                      onChange={e => setServiceForm(p => ({ ...p, warranty_days: e.target.value }))}
                      className="h-10 mt-1.5"
                      placeholder="14"
                      data-testid="warranty-days-input"
                    />
                  </div>
                  <div>
                    <Label>Warranty Terms (optional)</Label>
                    <Textarea
                      value={serviceForm.warranty_description}
                      onChange={e => setServiceForm(p => ({ ...p, warranty_description: e.target.value }))}
                      className="mt-1.5"
                      rows={2}
                      placeholder="What does the warranty cover?"
                      data-testid="warranty-desc-input"
                    />
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                    <p className="font-medium flex items-center gap-1">
                      <Info className="w-3 h-3" /> How warranty works
                    </p>
                    <ul className="mt-1 space-y-1 list-disc list-inside text-blue-700">
                      <li>Customer must pay via wallet or online (not cash)</li>
                      <li>Your earnings will be held until warranty expires</li>
                      <li>This protects both you and the customer</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            
            <Button onClick={handleSaveService} disabled={savingService} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="save-service-btn">
              <Save className="w-4 h-4 mr-2" /> {savingService ? 'Saving...' : editingService ? 'Update Service' : 'Add Service'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Add Certification Dialog */}
      <Dialog open={showCertDialog} onOpenChange={setShowCertDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Certification</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Certification Title</Label>
              <Input value={certForm.title} onChange={e => setCertForm(p => ({ ...p, title: e.target.value }))} className="h-12 mt-1.5" placeholder="e.g., TESDA NC II Plumbing" data-testid="cert-title-input" />
            </div>
            <div>
              <Label>Issuing Organization</Label>
              <Input value={certForm.issuer} onChange={e => setCertForm(p => ({ ...p, issuer: e.target.value }))} className="h-12 mt-1.5" placeholder="e.g., TESDA" data-testid="cert-issuer-input" />
            </div>
            <div>
              <Label>Year Obtained</Label>
              <Input value={certForm.year_obtained} onChange={e => setCertForm(p => ({ ...p, year_obtained: e.target.value }))} className="h-12 mt-1.5" placeholder="e.g., 2023" data-testid="cert-year-input" />
            </div>
            <Button onClick={handleAddCert} disabled={savingCert} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="save-cert-btn">
              <Save className="w-4 h-4 mr-2" /> {savingCert ? 'Saving...' : 'Add Certification'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingStaff ? 'Edit Staff' : 'Add Staff Member'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name</Label>
              <Input value={staffForm.name} onChange={e => setStaffForm(p => ({ ...p, name: e.target.value }))} className="h-12 mt-1.5" placeholder="Juan Dela Cruz" data-testid="staff-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input value={staffForm.email} onChange={e => setStaffForm(p => ({ ...p, email: e.target.value }))} className="h-12 mt-1.5" placeholder="juan@company.com" data-testid="staff-email-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={staffForm.phone} onChange={e => setStaffForm(p => ({ ...p, phone: e.target.value }))} className="h-12 mt-1.5" placeholder="0917..." data-testid="staff-phone-input" />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={staffForm.role} onValueChange={(v) => setStaffForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technician">Technician</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="helper">Helper</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Skills</Label>
              <Input value={staffForm.skills.join(', ')} onChange={e => setStaffForm(p => ({ ...p, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} className="h-12 mt-1.5" placeholder="Plumbing, Electrical" data-testid="staff-skills-input" />
              <p className="text-xs text-slate-400 mt-1">Comma-separated</p>
            </div>
            <Button onClick={handleSaveStaff} disabled={savingStaff} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="save-staff-btn">
              <Save className="w-4 h-4 mr-2" /> {savingStaff ? 'Saving...' : editingStaff ? 'Update Staff' : 'Add Staff'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <ProviderBottomNav />

      {/* Location Picker Modal */}
      <AddressPickerModal
        open={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSave={(addressData) => {
          setFormData(prev => ({
            ...prev,
            service_latitude: addressData.latitude,
            service_longitude: addressData.longitude,
            service_province: addressData.province || prev.service_province,
            service_city: addressData.city || prev.service_city,
            service_barangay: addressData.barangay || prev.service_barangay
          }));
          setShowLocationPicker(false);
          toast.success('Location updated!');
        }}
        initialAddress={{
          latitude: formData.service_latitude,
          longitude: formData.service_longitude,
          province: formData.service_province,
          city: formData.service_city,
          barangay: formData.service_barangay,
          label: 'Service Location',
          full_address: ''
        }}
      />

      {/* Accident Insurance Dialog */}
      <Dialog open={showInsuranceDialog} onOpenChange={setShowInsuranceDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="break-words">Provider Accident Insurance</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {insuranceSettings && (
              <>
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm font-semibold text-green-900 mb-2">Your Coverage Includes:</p>
                  <ul className="text-sm text-green-800 space-y-1">
                    {insuranceSettings.provider_covers_injuries && (
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                        <span>Injury Protection During Service</span>
                      </li>
                    )}
                    {insuranceSettings.provider_covers_equipment_damage && (
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                        <span>Equipment Damage Coverage</span>
                      </li>
                    )}
                    {insuranceSettings.provider_covers_third_party && (
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                        <span>Third-Party Liability</span>
                      </li>
                    )}
                    <li className="flex items-center gap-2 mt-2 pt-2 border-t border-green-200">
                      <span className="font-medium">Coverage Limit:</span>
                      <span>₱{insuranceSettings.provider_coverage_limit?.toLocaleString() || '10,000'}</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Terms & Conditions</h4>
                  <div 
                    className="prose prose-sm max-w-none provider-insurance-terms"
                    dangerouslySetInnerHTML={{ 
                      __html: insuranceSettings.provider_terms || 'This insurance provides coverage for accidents and injuries that may occur during service delivery. Please contact support to file a claim within 24 hours of any incident.' 
                    }}
                  />
                  <style>{`
                    .provider-insurance-terms {
                      font-size: 13px;
                      line-height: 1.6;
                      color: #374151;
                      word-wrap: break-word;
                      overflow-wrap: break-word;
                      word-break: normal;
                      white-space: normal;
                      max-width: 100%;
                      width: 100%;
                    }
                    .provider-insurance-terms * {
                      max-width: 100%;
                      word-wrap: break-word;
                      overflow-wrap: break-word;
                      white-space: normal;
                    }
                    .provider-insurance-terms h1, 
                    .provider-insurance-terms h2, 
                    .provider-insurance-terms h3 {
                      font-size: 15px;
                      font-weight: 600;
                      margin: 12px 0 6px 0;
                      color: #1e293b;
                    }
                    .provider-insurance-terms p {
                      margin: 8px 0;
                    }
                    .provider-insurance-terms ul, 
                    .provider-insurance-terms ol {
                      margin: 8px 0;
                      padding-left: 20px;
                    }
                    .provider-insurance-terms li {
                      margin: 4px 0;
                    }
                  `}</style>
                </div>
                
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800 break-words">
                    <strong>Note:</strong> In case of an accident, please file a claim within 24 hours through the Help & Support section.
                  </p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
